import React, { useState } from 'react';
import type { Card } from "../../gameboardTypes.tsx";
import StaticCard from '../../staticCard.tsx';
import { PlayingCard } from '../../../games/cardComponent.tsx';
import useWindowDimensions from '../../../../util/windowUtils.ts';
import { createText } from '../../../../util/cardEffects/cardText.ts';
import { cardHasEffectType } from '../../../../util/cardEffects/effectProcessing.tsx';
import useCardDimensions from '../../../../util/cardUtils.ts';

interface BattlefieldProps {
    cards: Card[],
    x: number,
    y: number,
    maxWidth: number,
    battlefieldCardScale: number;
    onCardClick?: (cardId: string) => void;
    clickable?: (cardId: string) => void;
}

export default function Battlefield({ cards, x, y, maxWidth, battlefieldCardScale, onCardClick, clickable }: BattlefieldProps) {
    const { width, height } = useWindowDimensions();
    const handCardScale = battlefieldCardScale;
    const hoveredCardScale = 1;
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const { cardWidth, cardHeight } = useCardDimensions(1);

    const count = cards?.length;

    const totalCardsWidth = count * cardWidth * handCardScale;
    const spacing = count > 1 && totalCardsWidth < maxWidth
        ? (maxWidth - totalCardsWidth) / (count - 1)
        : 0;

    const actualWidth = Math.min(totalCardsWidth + spacing * (count - 1), maxWidth);
    const startX = x + (maxWidth - actualWidth) / 2;

    let inspect: any = null;

    const handElements = cards?.map((card: Card, index) => {
        const cardY = y;
        const cardX = startX - (cardWidth * handCardScale / 2) + ((index + 1) * (actualWidth / (count + 1)));

        const isHovered = hoveredId === card.id;
        inspect = inspect ? inspect : isHovered && (
            <StaticCard
                key={`hovered-${x}-${y}-${card.name}`}
                X={width - (cardWidth * hoveredCardScale)}
                Y={(height - (cardHeight * hoveredCardScale)) / 2}
            >
                <PlayingCard
                    scale={hoveredCardScale}
                    name={card.name}
                    school={card.school}
                    cost={card.cost}
                    castThreshold={card.castThreshold}
                    ritual={cardHasEffectType("ritual", card)}
                    highlightOnFullFocus={true}

                    focus={card.focus.count}
                    tempFocus={card.focus.temp_count}

                    play={createText(card.playAbility)}
                    sustain={createText(card.sustainAbility)}
                    cast={createText(card.castAbility)}
                    passive={createText(card.passiveAbility)}
                />
            </StaticCard>
        )

        return (

            <StaticCard
                key={`battlefield-${card.id}-${width}-${count}-${cardX}-${cardY}`}
                X={cardX}
                Y={cardY}
                onMouseEnter={() => setHoveredId(card.id)}
                onMouseLeave={() => setHoveredId(null)}
            >
                <div
                    onClick={() => { if (onCardClick && clickable && clickable(card.id)) onCardClick(card.id); }}
                >
                    <PlayingCard
                        name={card?.name}
                        cost={card?.cost}
                        castThreshold={card?.castThreshold}
                        focus={card?.focus?.count}
                        tempFocus={card?.focus?.temp_count}
                        school={card?.school ?? "Manifest"}
                        classCard={card?.classCard}
                        ritual={cardHasEffectType("ritual", card)}
                        play={createText(card.playAbility)}
                        cast={createText(card.castAbility)}
                        sustain={createText(card.sustainAbility)}
                        passive={createText(card.passiveAbility)}
                        verticalIndex={index}
                        scale={handCardScale}
                    />
                    <div
                        className={clickable && clickable(card.id) ? 'target' : ''}
                        style={{ position: 'absolute', width: `100%`, top: `0`, zIndex: 1000, height: `100%` }}
                    />
                </div>

            </StaticCard >
        );
    });

    return (<div>
        {handElements}
        {inspect}
    </ div>)
}